(() => {
  if (customElements.get('haiven-card-restock')) return;
  const labels = document.currentScript?.dataset || {};
  const excludedRestockIds = new Set(["15429521768611", "15429521997987", "15429522227363", "15429522423971", "15429522489507", "15429523439779", "15429523669155", "15429524193443", "15429524258979", "15429524324515"]);
  const isInbound = product => (Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',')).some(tag => String(tag).trim().toLowerCase() === 'fresh-inbound-new');
  const restockEligible = product => {
    if (isInbound(product)) return false;
    if (!product || excludedRestockIds.has(String(product.id))) return false;
    if (String(product.type || product.product_type || '').toLowerCase().includes('planter')) return false;
    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
    return !tags.some(tag => ['made-to-order', 'restock-timing-unconfirmed', 'no-restock-estimate'].includes(String(tag).trim().toLowerCase().replace(/[_ ]/g, '-')));
  };
  const elements = new Set();
  const cache = new Map();
  const queue = [];
  let active = 0;
  const drain = () => {
    while (active < 4 && queue.length) {
      active++;
      const { url, resolve } = queue.shift();
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal })
        .then(response => response.ok ? response.json() : null)
        .catch(() => null)
        .then(resolve)
        .finally(() => { clearTimeout(timeout); active--; drain(); });
    }
  };
  const readProduct = (url) => {
    const entry = cache.get(url);
    if (entry && Date.now() - entry.time < 45000) return entry.promise;
    if (cache.size > 256) cache.clear();
    const promise = new Promise(resolve => { queue.push({ url, resolve }); drain(); });
    cache.set(url, { time: Date.now(), promise });
    return promise;
  };
  class CardRestock extends HTMLElement {
    static get observedAttributes() { return ['data-product-id', 'data-product-url']; }
    connectedCallback() {
      elements.add(this);
      this.revision = (this.revision || 0) + 1;
      const target = this.closest('.card-wrapper, .predictive-search__list-item, [data-hf-daily-slot]') || this.parentElement;
      if ('IntersectionObserver' in window && target) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries.some(entry => entry.isIntersecting);
          if (this.inView) this.refresh();
        }, { rootMargin: '100px' });
        this.observer.observe(target);
      } else {
        this.inView = true;
        this.refresh();
      }
    }
    disconnectedCallback() {
      elements.delete(this);
      this.revision++;
      this.observer?.disconnect();
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue || !this.isConnected) return;
      this.revision++;
      this.hidden = true;
      this.dataset.available = 'unknown';
      queueMicrotask(() => this.refresh());
    }
    async refresh() {
      if (!this.isConnected || document.hidden || !this.inView) return;
      const id = this.dataset.productId;
      const path = this.dataset.productUrl;
      if (!id || !path) { this.hidden = true; return; }
      const revision = ++this.revision;
      try {
        const url = new URL(path, location.origin);
        if (url.origin !== location.origin || !url.pathname.includes('/products/')) { this.hidden = true; return; }
        url.search = '';
        url.hash = '';
        url.pathname = url.pathname.replace(/\/$/, '') + '.js';
        const data = await readProduct(url.href);
        if (!this.isConnected || revision !== this.revision) return;
        const known = data && String(data.id) === id && typeof data.available === 'boolean';
        this.dataset.available = known ? String(data.available) : 'unknown';
        const inbound = known && isInbound(data);
        const english = this.querySelector('span[dir="ltr"]');
        if (english) english.textContent = inbound ? labels.inboundEn : labels.restockEn;
        this.hidden = !known || data.available !== false || (!inbound && !restockEligible(data));
      } catch { if (revision === this.revision) this.hidden = true; }
    }
  }
  customElements.define('haiven-card-restock', CardRestock);
  const refreshVisible = () => { if (!document.hidden) elements.forEach(element => element.refresh()); };
  setInterval(refreshVisible, 60000);
  document.addEventListener('visibilitychange', refreshVisible);
  window.addEventListener('pageshow', refreshVisible);
  const syncSlides = () => {
    document.querySelectorAll('[data-hf-daily-slot]').forEach(slide => {
      const media = slide.querySelector('.slideshow__media');
      const link = slide.querySelector('[data-hf-product-link]');
      if (!media || !link) return;
      let notice = media.querySelector('haiven-card-restock');
      const id = slide.dataset.hfProductId || '';
      const url = link.hidden ? '' : link.getAttribute('href') || '';
      if (!notice && id && url && labels.restockEn && labels.restockAr) {
        notice = document.createElement('haiven-card-restock');
        notice.hidden = true;
        notice.setAttribute('role', 'status');
        notice.setAttribute('aria-live', 'polite');
        notice.setAttribute('aria-atomic', 'true');
        const english = document.createElement('span');
        english.dir = 'ltr'; english.textContent = labels.restockEn;
        const arabic = document.createElement('bdi');
        arabic.dir = 'rtl'; arabic.lang = 'ar'; arabic.textContent = labels.restockAr;
        notice.append(english, document.createTextNode(' '), arabic);
        notice.dataset.productId = id;
        notice.dataset.productUrl = url;
        media.prepend(notice);
      } else if (notice) {
        if (notice.dataset.productId !== id) notice.dataset.productId = id;
        if (notice.dataset.productUrl !== url) notice.dataset.productUrl = url;
        if ((!id || !url) && !notice.hidden) notice.hidden = true;
      }
    });
  };
  let scheduled = false;
  const scheduleSlides = () => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => { scheduled = false; syncSlides(); });
  };
  const start = () => {
    syncSlides();
    new MutationObserver(scheduleSlides).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-hf-product-id', 'href', 'hidden'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

